import { CHALLENGE_TYPE, USER_TYPE_PERSONA } from "../utils/Constants";

// Maps a `{ total, per_persona: { fleet_owner, single_owner, driver } }`
// bucket (as returned by `users_list_analytics`) into
// `{ total, perPersona: { fo, so, driver } }`.
export const mapPersonaBucket = (bucket) => {
  const perPersona = Object.entries(bucket?.per_persona || {}).reduce(
    (acc, [userType, count]) => {
      acc[USER_TYPE_PERSONA[userType] || userType] = count;
      return acc;
    },
    {}
  );
  return { total: bucket?.total ?? 0, perPersona };
};

export class ChallengeTemplate {
  constructor(
    id,
    nameEn,
    nameAr,
    challengeType,
    userType,
    supportsDuringPromo,
    valueType
  ) {
    this.id = id;
    this.nameEn = nameEn;
    this.nameAr = nameAr;
    this.challengeType = challengeType;
    this.userType = userType;
    this.supportsDuringPromo = supportsDuringPromo;
    this.valueType = valueType;
  }

  static setChallengeTemplate(template) {
    return new ChallengeTemplate(
      template.id,
      template.name_en,
      template.name_ar,
      template.challenge_type,
      template.user_type,
      template.supports_during_promo,
      template.value_type
    );
  }

  static setChallengeTemplates(list = []) {
    return list.map(ChallengeTemplate.setChallengeTemplate);
  }
}

export class SubTask {
  constructor(
    id,
    titleEn,
    titleAr,
    challengeTemplateId,
    targetValue,
    withinPromoDuration,
    order,
    challengeTemplate
  ) {
    this.id = id;
    this.titleEn = titleEn;
    this.titleAr = titleAr;
    this.challengeTemplateId = challengeTemplateId;
    this.targetValue = targetValue;
    this.withinPromoDuration = withinPromoDuration;
    this.order = order;
    this.challengeTemplate = challengeTemplate;
  }

  static setSubTask(subTask) {
    return new SubTask(
      subTask.id,
      subTask.title_en,
      subTask.title_ar,
      subTask.challenge_template_id,
      subTask.value,
      subTask.during_promo,
      subTask.order,
      ChallengeTemplate.setChallengeTemplate(subTask.challenge_template)
    );
  }

  static setSubTasks(list = []) {
    return list.map(SubTask.setSubTask);
  }
}

export class ChallengeNotification {
  constructor(id, notificationType, titleEn, titleAr, bodyEn, bodyAr) {
    this.id = id;
    this.notificationType = notificationType;
    this.titleEn = titleEn;
    this.titleAr = titleAr;
    this.bodyEn = bodyEn;
    this.bodyAr = bodyAr;
  }

  static setChallengeNotification(notification) {
    return new ChallengeNotification(
      notification.id,
      notification.notification_type,
      notification.title_en,
      notification.title_ar,
      notification.body_en,
      notification.body_ar
    );
  }

  static setChallengeNotifications(list = []) {
    return list.map(ChallengeNotification.setChallengeNotification);
  }
}

export class Challenge {
  constructor(
    id,
    titleEn,
    titleAr,
    descriptionEn,
    descriptionAr,
    howToWinEn,
    howToWinAr,
    ctaEn,
    ctaAr,
    startDate,
    endDate,
    status,
    numberOfWinners,
    rewardType,
    rewardValue,
    rewardImageUrl,
    isPublished,
    targetPersonas,
    subTasks,
    notifications,
    rewardsSubmittedAt
  ) {
    this.id = id;
    this.titleEn = titleEn;
    this.titleAr = titleAr;
    this.descriptionEn = descriptionEn;
    this.descriptionAr = descriptionAr;
    this.howToWinEn = howToWinEn;
    this.howToWinAr = howToWinAr;
    this.ctaEn = ctaEn;
    this.ctaAr = ctaAr;
    this.startDate = startDate;
    this.endDate = endDate;
    this.status = status;
    this.numberOfWinners = numberOfWinners;
    this.rewardType = rewardType;
    this.rewardValue = rewardValue;
    this.rewardImageUrl = rewardImageUrl;
    this.isPublished = isPublished;
    this.targetPersonas = targetPersonas || [];
    this.subTasks = subTasks || [];
    this.notifications = notifications || [];
    this.challengeType = CHALLENGE_TYPE.quick; // Default to quick challenge type
    this.rewardsSubmittedAt = rewardsSubmittedAt || null;
  }

  static setTargetPersonas(personas = []) {
    return personas.map((persona) => USER_TYPE_PERSONA[persona] || persona);
  }

  static setChallenge(challenge) {
    return new Challenge(
      challenge.id,
      challenge.title_en,
      challenge.title_ar,
      challenge.description_en,
      challenge.description_ar,
      challenge.how_to_win_en,
      challenge.how_to_win_ar,
      challenge.cta_en,
      challenge.cta_ar,
      challenge.start_date,
      challenge.end_date,
      challenge.status,
      challenge.winner_cap,
      challenge.reward_type,
      challenge.reward_value,
      challenge.image_url,
      challenge.is_published,
      Challenge.setTargetPersonas(challenge.target_personas),
      SubTask.setSubTasks(challenge.sub_tasks || []),
      ChallengeNotification.setChallengeNotifications(
        challenge.challenge_notifications || []
      ),
      challenge.rewards_submitted_at || null
    );
  }

  static setChallenges(list = []) {
    return list.map(Challenge.setChallenge);
  }
}

export class ChallengeUser {
  constructor(
    id,
    userId,
    rank,
    displayName,
    persona,
    phoneNumber,
    completedAt,
    winnerStatus,
    isSelected,
    fraudFlag
  ) {
    this.id = id;
    this.userId = userId;
    this.rank = rank;
    this.displayName = displayName;
    this.persona = persona;
    this.phoneNumber = phoneNumber;
    this.completedAt = completedAt;
    this.winnerStatus = winnerStatus;
    this.isSelected = isSelected;
    this.fraudFlag = fraudFlag;
  }

  static setChallengeUser(user) {
    return new ChallengeUser(
      user.id,
      user.owner_id || user.driver_id,
      user.rank,
      user.display_name,
      USER_TYPE_PERSONA[user.user_type] || user.user_type,
      user.phone_number,
      user.completed_at,
      user.outcome,
      !!user.is_selected,
      {
        flagged: !!user.is_red_flagged,
        source: user.fraud_source || null,
      }
    );
  }

  static setChallengeUsers(list = []) {
    return list.map(ChallengeUser.setChallengeUser);
  }
}

export class ChallengeUsersAnalytics {
  constructor(winners, nonWinners, totalCompleted, capacityRemaining, deletedWinnersCount) {
    this.winners = winners;
    this.nonWinners = nonWinners;
    this.totalCompleted = totalCompleted;
    this.capacityRemaining = capacityRemaining;
    this.deletedWinnersCount = deletedWinnersCount;
  }

  static setChallengeUsersAnalytics(analytics) {
    if (!analytics) return null;
    return new ChallengeUsersAnalytics(
      mapPersonaBucket(analytics.winners),
      mapPersonaBucket(analytics.non_winners),
      mapPersonaBucket(analytics.completions),
      analytics.capacity_remaining ?? 0,
      analytics.deleted_winners_count ?? 0
    );
  }
}

export class ChallengeReward {
  constructor(
    id,
    userId,
    displayName,
    persona,
    phoneNumber,
    isRedFlagged,
    rewardValue,
    rewardType,
    status,
    selectedAt,
    redeemedAt,
    selectionExpiresAt,
    redemptionExpiresAt,
    createdAt
  ) {
    this.id = id;
    this.userId = userId;
    this.displayName = displayName;
    this.persona = persona;
    this.phoneNumber = phoneNumber;
    this.isRedFlagged = isRedFlagged;
    this.rewardValue = rewardValue;
    this.rewardType = rewardType;
    this.status = status;
    this.selectedAt = selectedAt;
    this.redeemedAt = redeemedAt;
    this.selectionExpiresAt = selectionExpiresAt;
    this.redemptionExpiresAt = redemptionExpiresAt;
    this.createdAt = createdAt;
  }

  // Maps a row from GET /admin_portal/v1/quick_challenge_rewards.
  static setChallengeReward(reward) {
    return new ChallengeReward(
      reward.id,
      reward.owner_id || reward.driver_id,
      reward.display_name,
      USER_TYPE_PERSONA[reward.user_type] || reward.user_type,
      reward.phone_number,
      reward.is_red_flagged || false,
      reward.reward_value,
      reward.reward_type,
      reward.status,
      reward.selected_at,
      reward.redeemed_at,
      reward.selection_expires_at,
      reward.redemption_expires_at,
      reward.created_at
    );
  }

  static setChallengeRewards(list = []) {
    return list.map(ChallengeReward.setChallengeReward);
  }
}

export class ChallengeRewardsAnalytics {
  constructor(
    totalEarned,
    totalRedeemed,
    pendingSelection,
    selected,
    expiredNotSelected,
    expiredNotRedeemed,
    personaBreakdown
  ) {
    this.totalEarned = totalEarned;
    this.totalRedeemed = totalRedeemed;
    this.pendingSelection = pendingSelection;
    this.selected = selected;
    this.expiredNotSelected = expiredNotSelected;
    this.expiredNotRedeemed = expiredNotRedeemed;
    // One row per targeted persona (BE `persona_breakdown`); null for
    // single-persona challenges.
    this.personaBreakdown = personaBreakdown || null;
  }

  static setChallengeRewardsAnalytics(analytics) {
    if (!analytics) return null;
    return new ChallengeRewardsAnalytics(
      analytics.total_earned ?? 0,
      analytics.total_redeemed ?? 0,
      analytics.pending_selection ?? 0,
      analytics.selected ?? 0,
      analytics.expired_not_selected ?? 0,
      analytics.expired_not_redeemed ?? 0,
      analytics.persona_breakdown
        ? analytics.persona_breakdown.map((row) => ({
            persona: USER_TYPE_PERSONA[row.user_type] || row.user_type,
            totalEarned: row.total_earned ?? 0,
            totalRedeemed: row.total_redeemed ?? 0,
            pendingSelection: row.pending_selection ?? 0,
            selected: row.selected ?? 0,
            expiredNotSelected: row.expired_not_selected ?? 0,
            expiredNotRedeemed: row.expired_not_redeemed ?? 0,
          }))
        : null
    );
  }
}
